// src/hooks/useChildProfile.ts
// TanStack Query hooks for child profile data

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/core/lib/api';
import type { Child, ChildDiagnosis, Appointment, Medication, CareTeamMember, HealthLog } from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface ChildProfile extends Child {
  diagnoses: ChildDiagnosis[];
  primaryDiagnosis?: ChildDiagnosis;
  upcomingAppointments: Appointment[];
  recentHealthLogs: HealthLog[];
  currentMedications: Medication[];
  careTeam: CareTeamMember[];
  stats: ChildProfileStats;
}

export interface ChildProfileStats {
  totalAppointments: number;
  upcomingAppointmentsCount: number;
  activeGoalsCount: number;
  completedGoalsCount: number;
  documentsCount: number;
  careTeamMembersCount: number;
  daysInCare: number;
  lastHealthLogDate?: string;
  seizureFreeStreak?: number;
}

export interface ProfileActivity {
  id: string;
  type: 'health_log' | 'appointment' | 'document' | 'goal' | 'medication';
  title: string;
  description?: string;
  timestamp: string;
  icon?: string;
  color?: string;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Query Keys
// ============================================================================

export const childProfileKeys = {
  all: ['child-profile'] as const,
  detail: (childId: string) => [...childProfileKeys.all, 'detail', childId] as const,
  stats: (childId: string) => [...childProfileKeys.all, 'stats', childId] as const,
  activity: (childId: string) => [...childProfileKeys.all, 'activity', childId] as const,
  diagnoses: (childId: string) => [...childProfileKeys.all, 'diagnoses', childId] as const,
  appointments: (childId: string) => [...childProfileKeys.all, 'appointments', childId] as const,
  medications: (childId: string) => [...childProfileKeys.all, 'medications', childId] as const,
  careTeam: (childId: string) => [...childProfileKeys.all, 'careTeam', childId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch complete child profile with all related data
 */
export function useChildProfile(childId: string | undefined) {
  return useQuery({
    queryKey: childProfileKeys.detail(childId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<ChildProfile>(`/children/${childId}/profile`);
      return response.data;
    },
    enabled: !!childId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Fetch child profile stats only
 */
export function useChildProfileStats(childId: string | undefined) {
  return useQuery({
    queryKey: childProfileKeys.stats(childId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<ChildProfileStats>(`/children/${childId}/stats`);
      return response.data;
    },
    enabled: !!childId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch recent activity for a child
 */
export function useChildActivity(
  childId: string | undefined,
  options?: { limit?: number; types?: string[] }
) {
  return useQuery({
    queryKey: [...childProfileKeys.activity(childId ?? ''), options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', String(options.limit));
      if (options?.types) params.append('types', options.types.join(','));

      const response = await apiClient.get<ProfileActivity[]>(
        `/children/${childId}/activity?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!childId,
    staleTime: 1000 * 60 * 1, // 1 minute
  });
}

/**
 * Fetch child's diagnoses
 */
export function useChildDiagnoses(childId: string | undefined) {
  return useQuery({
    queryKey: childProfileKeys.diagnoses(childId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<ChildDiagnosis[]>(`/children/${childId}/diagnoses`);
      return response.data;
    },
    enabled: !!childId,
    staleTime: 1000 * 60 * 10, // 10 minutes - diagnoses change infrequently
  });
}

/**
 * Fetch child's upcoming appointments
 */
export function useChildAppointments(
  childId: string | undefined,
  options?: { upcoming?: boolean; limit?: number }
) {
  return useQuery({
    queryKey: [...childProfileKeys.appointments(childId ?? ''), options],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.upcoming) params.append('upcoming', 'true');
      if (options?.limit) params.append('limit', String(options.limit));

      const response = await apiClient.get<Appointment[]>(
        `/children/${childId}/appointments?${params.toString()}`
      );
      return response.data;
    },
    enabled: !!childId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetch child's current medications
 */
export function useChildMedications(childId: string | undefined) {
  return useQuery({
    queryKey: childProfileKeys.medications(childId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<Medication[]>(
        `/children/${childId}/medications?active=true`
      );
      return response.data;
    },
    enabled: !!childId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Fetch child's care team
 */
export function useChildCareTeam(childId: string | undefined) {
  return useQuery({
    queryKey: childProfileKeys.careTeam(childId ?? ''),
    queryFn: async () => {
      const response = await apiClient.get<CareTeamMember[]>(`/children/${childId}/care-team`);
      return response.data;
    },
    enabled: !!childId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

// ============================================================================
// Mutations
// ============================================================================

/**
 * Update child avatar
 */
export function useUpdateChildAvatar(childId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await apiClient.post<{ avatarUrl: string }>(
        `/children/${childId}/avatar`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: childProfileKeys.detail(childId) });
      queryClient.invalidateQueries({ queryKey: ['children'] });
    },
  });
}

/**
 * Generate emergency card for child
 */
export function useGenerateEmergencyCard(childId: string) {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{ downloadUrl: string }>(
        `/children/${childId}/emergency-card`
      );
      return response.data;
    },
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Prefetch child profile data
 */
export function usePrefetchChildProfile(childId: string | undefined) {
  const queryClient = useQueryClient();

  return () => {
    if (!childId) return;

    queryClient.prefetchQuery({
      queryKey: childProfileKeys.detail(childId),
      queryFn: async () => {
        const response = await apiClient.get<ChildProfile>(`/children/${childId}/profile`);
        return response.data;
      },
      staleTime: 1000 * 60 * 5,
    });
  };
}

export default {
  useChildProfile,
  useChildProfileStats,
  useChildActivity,
  useChildDiagnoses,
  useChildAppointments,
  useChildMedications,
  useChildCareTeam,
  useUpdateChildAvatar,
  useGenerateEmergencyCard,
  usePrefetchChildProfile,
};

